import React, { useState } from 'react';
import { X, CheckSquare } from 'lucide-react';
import { activityStorage } from '../../services/activityStorage';
import { persistedTaskRecords } from '../../services/mockData/tasks';
import { RecordType } from '../../types/activity';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { TIMEZONE_OPTIONS, detectLocalTimezone } from '../common/TimeWithLocalHover';

interface LogTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  recordId: string;
  recordType: RecordType;
  contactName?: string;
  onTaskLogged?: () => void;
}

type TaskPriority = 'High' | 'Medium' | 'Low';
type TaskStatus = 'Open' | 'In Progress' | 'Completed';
type TaskType = 'Follow-up' | 'Action Item' | 'Review' | 'Documentation' | 'Consultation' | 'Other';

const taskTypes: { value: TaskType; label: string }[] = [
  { value: 'Follow-up', label: 'Follow-up' },
  { value: 'Action Item', label: 'Action Item' },
  { value: 'Review', label: 'Review' },
  { value: 'Documentation', label: 'Documentation' },
  { value: 'Consultation', label: 'Consultation' },
  { value: 'Other', label: 'Other' },
];

const taskPriorities: { value: TaskPriority; label: string }[] = [
  { value: 'High', label: 'High' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Low', label: 'Low' },
];

const taskStatuses: { value: TaskStatus; label: string }[] = [
  { value: 'Open', label: 'Open' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Completed', label: 'Completed' },
];

export const LogTaskModal: React.FC<LogTaskModalProps> = ({
  isOpen,
  onClose,
  recordId,
  recordType,
  contactName,
  onTaskLogged,
}) => {
  const { userId, userName } = useCurrentUser();
  const [title, setTitle] = useState('');
  const [taskType, setTaskType] = useState<TaskType>('Follow-up');
  const [priority, setPriority] = useState<TaskPriority>('Medium');
  const [status, setStatus] = useState<TaskStatus>('Open');
  const [dueDate, setDueDate] = useState('');
  const [assignee, setAssignee] = useState(userName);
  const [description, setDescription] = useState('');
  const [timezone, setTimezone] = useState(detectLocalTimezone);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      setError('Please enter a task title');
      return;
    }

    try {
      const taskId = `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const now = new Date().toISOString();

      // 1. Create Task record (for Tasks tab, My Work page)
      const newTask = {
        id: taskId,
        clientId: recordId,
        title,
        type: taskType,
        status,
        priority,
        assignee,
        dueDate: dueDate || now.split('T')[0],
        slaTarget: 24, // Default SLA
        source: 'Manual',
      };

      persistedTaskRecords.add(newTask);

      // 2. Create Activity record (for Activity Timeline) - async for Gobodo API
      await activityStorage.create({
        type: 'task',
        recordId,
        recordType,
        userId,
        userName,
        subject: `Task: ${title}`,
        description: `${taskType} | Priority: ${priority} | Status: ${status}${dueDate ? ` | Due: ${dueDate}` : ''}${description ? `\n\n${description}` : ''}`,
        source: 'manual',
        timezone,
      });

      // Reset form
      setTitle('');
      setTaskType('Follow-up');
      setPriority('Medium');
      setStatus('Open');
      setDueDate('');
      setAssignee(userName);
      setDescription('');
      setTimezone(detectLocalTimezone());
      setError('');

      onTaskLogged?.();
      onClose();
    } catch (err) {
      console.error('[LogTaskModal] Error creating task:', err);
      setError('Failed to create task. Please try again.');
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  // Calculate default due date (tomorrow)
  const getDefaultDueDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Log Task
            </h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Related To (read-only) */}
            {contactName && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Related To
                </label>
                <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 px-3 py-2 rounded-md">
                  {contactName}
                </div>
              </div>
            )}

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Task Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => { setTitle(e.target.value); setError(''); }}
                placeholder="e.g., Send follow-up proposal"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Type & Priority Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Type
                </label>
                <select
                  value={taskType}
                  onChange={(e) => setTaskType(e.target.value as TaskType)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {taskTypes.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Priority
                </label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as TaskPriority)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {taskPriorities.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Status & Due Date Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as TaskStatus)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {taskStatuses.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Due Date
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  placeholder={getDefaultDueDate()}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Assignee & Timezone Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Assignee
                </label>
                <input
                  type="text"
                  value={assignee}
                  onChange={(e) => setAssignee(e.target.value)}
                  placeholder="Assign to..."
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Timezone
                </label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} ({tz.abbr})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Task details..."
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>

            {/* Error */}
            {error && (
              <p className="text-sm text-red-500">{error}</p>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <CheckSquare className="w-4 h-4" />
                Create Task
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
